import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, LOCALE_ID, resource } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  formatRequestDate,
  SOFTLINE_API_PMS_ROSTER_EMPLOYEE_WORKSCHEMA,
  Workschema,
} from '@softapps/pms/roster';
import {
  AuthenticationContextStore,
  SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
} from '@softline/auth';
import {
  ApiDataAccess,
  ApiResourceLocation,
  DateService,
  endOfWeek,
  IsoDate,
  startOfWeek,
  Store,
} from '@softline/core';
import { catchError, of } from 'rxjs';

const DEFAULT_DAY_TARGET_HOURS = 8.5;
const DEFAULT_WEEK_TARGET_HOURS = 38.5;

interface PublicHolidayDto {
  startDate: string;
  endDate?: string;
  name?: { text: string }[];
}

@Injectable({ providedIn: 'root' })
export class WorkHoursTargetService {
  readonly #dataAccess = inject(ApiDataAccess);
  readonly #dateService = inject(DateService);
  readonly #locale = inject(LOCALE_ID);
  readonly #store = inject(Store);
  readonly #publicHolidayHttp = new HttpClient(inject(HttpBackend));

  readonly #context = this.#store.signal(
    SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
    AuthenticationContextStore.getters.data
  );

  readonly #employeeId = computed<number | null>(() => {
    const id = (this.#context() as { personalid?: number } | null | undefined)
      ?.personalid;
    return typeof id === 'number' ? id : null;
  });

  readonly #workschema = resource({
    params: () => ({ employeeId: this.#employeeId() }),
    loader: async ({ params }): Promise<Workschema | null> => {
      if (!params.employeeId) return null;
      const location: ApiResourceLocation = {
        path: SOFTLINE_API_PMS_ROSTER_EMPLOYEE_WORKSCHEMA,
        pathParams: { employeeId: params.employeeId },
        queryParams: {
          cutoffdate: formatRequestDate(this.#dateService.now(), this.#locale),
        },
      };
      try {
        return await this.#dataAccess.get<Workschema>(location);
      } catch {
        return null;
      }
    },
  });

  readonly #publicHolidays = rxResource<PublicHolidayDto[], { year: number }>({
    params: () => ({ year: new Date(this.#dateService.now()).getFullYear() }),
    stream: ({ params }) =>
      this.#publicHolidayHttp
        .get<PublicHolidayDto[]>('https://openholidaysapi.org/PublicHolidays', {
          params: {
            countryIsoCode: 'AT',
            subdivisionCode: 'ST',
            languageIsoCode: 'DE',
            validFrom: `${params.year}-01-01`,
            validTo: `${params.year}-12-31`,
          },
          headers: new HttpHeaders({ accept: 'text/json' }),
        })
        // External holiday API — flaky/offline-friendly. Swallow failures and
        // fall back to no holidays so target hours still compute.
        .pipe(catchError(() => of([] as PublicHolidayDto[]))),
  });

  readonly #holidayDateSet = computed<Set<string>>(() => {
    const list = this.#publicHolidays.value() ?? [];
    const set = new Set<string>();
    for (const holiday of list) {
      const start = holiday.startDate;
      const end = holiday.endDate ?? start;
      if (!start) continue;
      const startDate = new Date(start);
      const endDate = new Date(end);
      for (
        const d = new Date(startDate);
        d <= endDate;
        d.setDate(d.getDate() + 1)
      ) {
        set.add(d.toISOString().slice(0, 10));
      }
    }
    return set;
  });

  readonly dayTargetHours = computed<number>(() => {
    if (this.#isHoliday(this.#today())) return 0;
    const schema = this.#workschema.value();
    if (!schema) return DEFAULT_DAY_TARGET_HOURS;
    const weekday = this.#todayIsoWeekday();
    const entry = schema.detail.find((d) => d.weekday === weekday);
    if (!entry) return 0;
    return this.#scheduleEntryHours(entry.fromTime, entry.tillTime, entry.breakTime);
  });

  readonly weekTargetHours = computed<number>(() => {
    const schema = this.#workschema.value();
    if (!schema || schema.detail.length === 0) return DEFAULT_WEEK_TARGET_HOURS;
    const holidays = this.#holidayDateSet();
    const weekDates = this.#currentWeekDates();
    return schema.detail.reduce((sum, d) => {
      const dateIso = weekDates.get(d.weekday);
      if (dateIso && holidays.has(dateIso)) return sum;
      return sum + this.#scheduleEntryHours(d.fromTime, d.tillTime, d.breakTime);
    }, 0);
  });

  #today(): string {
    return new Date(this.#dateService.now()).toISOString().slice(0, 10);
  }

  #isHoliday(dateIso: string): boolean {
    return this.#holidayDateSet().has(dateIso);
  }

  #todayIsoWeekday(): number {
    const day = new Date(this.#dateService.now()).getDay();
    return day === 0 ? 7 : day;
  }

  #currentWeekDates(): Map<number, string> {
    const today = this.#dateService.now() as IsoDate;
    const start = new Date(startOfWeek(today));
    const end = new Date(endOfWeek(today));
    const map = new Map<number, string>();
    for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const weekday = d.getDay() === 0 ? 7 : d.getDay();
      map.set(weekday, d.toISOString().slice(0, 10));
    }
    return map;
  }

  #scheduleEntryHours(
    from: string,
    till: string,
    breakTime: string | null | undefined
  ): number {
    const work = this.#timeToHours(till) - this.#timeToHours(from);
    if (work <= 0) return 0;
    const declaredPause = this.#timeToHours(breakTime);
    const pause = declaredPause > 0 ? declaredPause : work >= 6 ? 1 : 0;
    return Math.max(0, work - pause);
  }

  #timeToHours(value: string | null | undefined): number {
    if (!value) return 0;
    const [h, m, s] = value.split(':').map((p) => Number(p) || 0);
    return (h ?? 0) + (m ?? 0) / 60 + (s ?? 0) / 3600;
  }
}
